import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { unitDefinitions } from '../../data/units';

const UnitConverter = ({ type }) => {
  const [inputValue, setInputValue] = useState('1');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [result, setResult] = useState('');
  const [showAllUnits, setShowAllUnits] = useState(false);

  const units = unitDefinitions[type] || [];

  useEffect(() => {
    if (units.length > 0) {
      setFromUnit(units[0].key);
      setToUnit(units[1]?.key || units[0].key);
    }
  }, [type, units]);

  const convertValue = () => {
    const value = parseFloat(inputValue);
    if (isNaN(value) || !fromUnit || !toUnit) {
      setResult('');
      return;
    }

    const fromUnitData = units.find(u => u.key === fromUnit);
    const toUnitData = units.find(u => u.key === toUnit);

    if (!fromUnitData || !toUnitData) {
      setResult('');
      return;
    }

    // Convert to base unit first, then to target unit
    const baseValue = value * fromUnitData.factor;
    const convertedValue = baseValue / toUnitData.factor;

    setResult(convertedValue.toLocaleString('de-DE', { 
      maximumFractionDigits: 10,
      minimumFractionDigits: 0
    }));
  };

  useEffect(() => {
    convertValue();
  }, [inputValue, fromUnit, toUnit]);

  const getAllConversions = () => {
    const value = parseFloat(inputValue);
    if (isNaN(value) || !fromUnit) return [];

    const fromUnitData = units.find(u => u.key === fromUnit);
    if (!fromUnitData) return [];

    const baseValue = value * fromUnitData.factor;

    return units.map(unit => ({
      unit: unit.name,
      value: (baseValue / unit.factor).toLocaleString('de-DE', {
        maximumFractionDigits: 10,
        minimumFractionDigits: 0
      })
    }));
  };

  if (units.length === 0) {
    return (
      <div className="text-white text-center">
        <p>Einheiten für diesen Konverter sind noch nicht verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="space-y-4">
        <div>
          <label className="block text-white font-medium mb-2">
            Wert
          </label>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Wert eingeben"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white font-medium mb-2">
              Von
            </label>
            <select
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              {units.map(unit => (
                <option key={unit.key} value={unit.key} className="bg-gray-800">
                  {unit.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white font-medium mb-2">
              Nach
            </label>
            <select
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              {units.map(unit => (
                <option key={unit.key} value={unit.key} className="bg-gray-800">
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10 text-center"
        >
          <h3 className="text-xl font-bold text-white mb-4">
            Ergebnis
          </h3>
          <div className="text-3xl font-bold text-purple-300 mb-2">
            {result}
          </div>
          <div className="text-white/70">
            {units.find(u => u.key === toUnit)?.name}
          </div>
        </motion.div>
      )}

      {/* All Units Table */}
      <div className="text-center">
        <button
          onClick={() => setShowAllUnits(!showAllUnits)}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
        >
          {showAllUnits ? 'Tabelle ausblenden' : 'Alle Einheiten anzeigen'}
        </button>
      </div>

      {showAllUnits && inputValue && fromUnit && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10"
        >
          <h3 className="text-xl font-bold text-white mb-4 text-center">
            {inputValue} {units.find(u => u.key === fromUnit)?.name} entspricht:
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {getAllConversions().map((conversion, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                <span className="text-white/70">{conversion.unit}</span>
                <span className="text-white font-medium">{conversion.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default UnitConverter;